import { LINKS } from '@fans-fund-me/shared-comm-react-cosmos/fans-fund-me/links';
import { Screen } from '@fans-fund-me/shared-comm-react-cosmos/fans-fund-me/register-profile-screen';
import { useAuth } from '@fans-fund-me/shared-common-esm-authentication';
import { useModalsStack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useFileDialog } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { updateProfile } from '../../api/profile.ts';
import { FORM_ERRORS } from '../../constants/form_errors.ts';

function RegisterProfile() {
  const navigate = useNavigate();
  const auth = useAuth();

  const fileDialog = useFileDialog({ multiple: false, accept: 'image/*' });
  const [imageType, setImageType] = useState<'cover-image' | 'profile-picture' | 'wishlist-item-image' | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const modalStack = useModalsStack(['crop-image-form']);

  const [isLoading, setIsLoading] = useState(false);

  interface FormValues {
    cover_image: Blob | string;
    profile_picture: Blob | string;
    display_name: string;
    bio: string;
  }

  const form = useForm<FormValues>({
    mode: 'uncontrolled',
    initialValues: {
      cover_image: '',
      profile_picture: '',
      display_name: '',
      bio: '',
    },
  });

  const blobToBase64 = (blob: Blob) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    return new Promise((resolve) => {
      reader.onloadend = () => {
        resolve((reader.result as string).split('base64,')[1]);
      };
    });
  };

  useEffect(() => {
    if (fileDialog.files && fileDialog.files.length > 0) {
      const imageFile = fileDialog.files[0];
      if (imageFile && modalStack.state['crop-image-form'] == false && imageFile !== image) {
        modalStack.open('crop-image-form');
        if (imageFile != image) {
          setImage(imageFile);
          fileDialog.reset();
        }
      }
    }
  }, [fileDialog.files, modalStack.state, image, fileDialog, modalStack]);

  const handleSubmit = async (values: typeof form.values) => {
    setIsLoading(true);

    if (typeof values.cover_image !== 'string' && values.cover_image.size > 5 * 1024 * 1024) {
      form.setErrors({
        cover_image: FORM_ERRORS.IMAGE_TOO_BIG,
      });
      return;
    }

    if (typeof values.profile_picture !== 'string' && values.profile_picture.size > 5 * 1024 * 1024) {
      form.setErrors({
        profile_picture: FORM_ERRORS.IMAGE_TOO_BIG,
      });
      return;
    }

    let profileData = {};

    if (typeof values.cover_image !== 'string') {
      profileData = {
        ...profileData,
        coverImage: await blobToBase64(values.cover_image),
      };
    }
    if (typeof values.profile_picture !== 'string') {
      profileData = {
        ...profileData,
        profilePicture: await blobToBase64(values.profile_picture),
      };
    }
    profileData = {
      ...profileData,
      displayName: values.display_name,
      bio: values.bio,
    };

    updateProfile(profileData, auth.id, auth.token)
      .then(() => {
        localStorage.removeItem('ffm_register_username');
        localStorage.removeItem('ffm_register_country');
        navigate(LINKS.REGISTER_STRIPE);
      })
      .catch((error) => {
        form.setErrors({
          display_name: error.response.data.fields.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'displayName',
          )?.message,
          bio: error.response.data.fields.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'bio',
          )?.message,
          cover_image: error.response.data.fields.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'coverImage',
          )?.message,
          profile_picture: error.response.data.fields.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'profilePic',
          ).message,
        });
        setIsLoading(false);
      });
  };

  return (
    <Screen
      form={form}
      handleSubmit={handleSubmit}
      isLoading={isLoading}
      fileDialog={fileDialog}
      imageType={imageType}
      setImageType={setImageType}
      modalStack={modalStack}
      defaultCoverImage={`https://fans-fund-me-core-profile-user-images-prod.s3.eu-west-2.amazonaws.com/cover_image/FFM-default-cover-image.png`}
      defaultProfilePicture={`https://fans-fund-me-core-profile-user-images-prod.s3.eu-west-2.amazonaws.com/profile_pic/FFM-default-profile-picture.png`}
    />
  );
}

export default RegisterProfile;
